import React, { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { X, ExclamationTriangle, Trash3 } from 'react-bootstrap-icons';
import { removeDiscountFromReceipt } from '../../../../../../../../../../../../../crud/payments_api/payment_receipts';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../../../../../../../../../../../../utils/functions';
import MiniLoadingSpinner from '../../../../../../../../../../../../../components/Spinners/MiniLoadingSpinner';
import './RemoveDiscountModal.scss';

interface RemoveDiscountModalProps {
  show: boolean;
  onHide: () => void;
  receiptId: number;
  currentDiscountAmount: number;
  totalCuenta: number;
  onDiscountRemoved: (removedAmount: number) => void;
}

const RemoveDiscountModal: React.FC<RemoveDiscountModalProps> = ({
  show,
  onHide,
  receiptId,
  currentDiscountAmount,
  totalCuenta,
  onDiscountRemoved,
}) => {
  const [loading, setLoading] = useState(false);
  const [amountToRemove, setAmountToRemove] = useState<number>(currentDiscountAmount);

  const handleRemoveDiscount = () => {
    if (loading) return;
    
    if (amountToRemove <= 0) {
      toast.error('El monto a remover debe ser mayor a 0.');
      return;
    }

    if (amountToRemove > currentDiscountAmount) {
      toast.error('El monto a remover no puede ser mayor al descuento actual.');
      return;
    }

    setLoading(true);

    removeDiscountFromReceipt(receiptId, amountToRemove)
      .then(() => {
        toast.success(`Descuento de ${formatCurrency(amountToRemove)} removido correctamente`);
        onDiscountRemoved(amountToRemove);
        onHide();
      })
      .catch((error) => {
        console.error('Error removing discount: ', error);
        toast.error('Error removiendo descuento');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClose = () => {
    if (!loading) {
      setAmountToRemove(currentDiscountAmount); // Reset to default
      onHide();
    }
  };

  if (loading) {
    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Body>
          <MiniLoadingSpinner left="50%" />
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      centered
      backdrop={loading ? "static" : true}
      keyboard={!loading}
      className="remove-discount-modal"
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            <Trash3 />
            Remover Descuento
          </Modal.Title>
        </div>
        <Button variant="none" onClick={handleClose} disabled={loading}>
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body>
        <div className="amount-input-section">
          {/* Current discount reference */}
          <div className="balance-reference">
            <span className="reference-label">Descuento actual aplicado:</span>
            <span className="reference-amount">{formatCurrency(currentDiscountAmount)}</span>
          </div>
          
          <div className="form-field">
            <div className="field-label">Monto a remover</div>
            <div className="amount-input-group">
              <div className="currency-symbol">$</div>
              <input
                type="text"
                className="amount-input"
                value={
                  amountToRemove > 0
                    ? amountToRemove.toLocaleString('es-CR', {
                        maximumFractionDigits: 2,
                      })
                    : ''
                }
                onChange={(e) => {
                  const onlyNums = e.target.value.replace(/[^\d.]/g, '');
                  const numValue = parseFloat(onlyNums) || 0;
                  if (numValue <= currentDiscountAmount) {
                    setAmountToRemove(numValue);
                  }
                }}
                placeholder="0.00"
                disabled={loading}
                autoFocus
              />
            </div>
          </div>
        </div>

        {/* Warning banner for removal confirmation */}
        {amountToRemove > 0 && (
          <>
            <div className="warning-banner removal-warning">
              <ExclamationTriangle className="warning-icon" />
              <div className="warning-content">
                <div className="warning-title">Confirmación de remoción</div>
                <div className="warning-message">
                  Se removerá un descuento de <span className="amount-highlight">{formatCurrency(amountToRemove)}</span>
                  {amountToRemove < currentDiscountAmount && (
                    <span>, quedando <span className="amount-highlight">{formatCurrency(currentDiscountAmount - amountToRemove)}</span> de descuento</span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="new-total-section">
              <span className="new-total-label">Nuevo total:</span>
              <span className="new-total-amount">{formatCurrency(totalCuenta - (currentDiscountAmount - amountToRemove))}</span>
            </div>
          </>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button
            variant="none"
            className="form-button-cancel"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="none"
            className="form-button-delete"
            onClick={handleRemoveDiscount}
            disabled={loading || amountToRemove <= 0}
          >
            Remover Descuento
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default RemoveDiscountModal;
